/**
 * ClaimedListingsWidget — shows on the user's own profile.
 * Lists any independent-provider directory listings they've claimed.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Award, ExternalLink, MapPin, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClaimedListingsWidget() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/independents/me/claimed`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setItems(r.data || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const unclaim = async (id) => {
    if (!window.confirm('Release this listing claim?')) return;
    setBusy(id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/independents/${id}/claim`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success('Claim released');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not release');
    } finally {
      setBusy(null);
    }
  };

  if (items === null) return null;
  if (items.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="claimed-listings-widget">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-700" />
          <h3 className="font-semibold text-sm uppercase tracking-wide text-amber-900">Claimed listings</h3>
          <span className="text-xs text-amber-700">({items.length})</span>
        </div>
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3 bg-white border border-amber-100 rounded-md p-3" data-testid={`claimed-item-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-stone-900 truncate">{p.name}</div>
                <div className="text-xs text-stone-500 flex items-center gap-1.5 mt-0.5">
                  {p.city && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}{p.state ? `, ${p.state}` : ''}</span>)}
                  <Badge variant="outline" className="text-xs">{p.ownership_type.replace('_', '-')}</Badge>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Link to={`/trusted-providers`} className="text-stone-400 hover:text-stone-700" title="View in directory">
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => unclaim(p.id)}
                  disabled={busy === p.id}
                  data-testid={`unclaim-${p.id}`}
                  className="text-stone-400 hover:text-red-600 h-7 w-7 p-0"
                  title="Release claim"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
